import { useEffect, useState } from "react";
import type { CctvCamera } from "@sikhio/shared";
import { embeddablePlayerUrl, parsePlayerUrl } from "../lib/cctvPlayer";
import { CctvMsePlayer } from "./CctvMsePlayer";

interface Props {
  camera: CctvCamera | null;
  onClose: () => void;
}

/**
 * Unified CCTV live-view modal — handles every vendor the dashboard
 * ingests so that *clicking any CCTV marker on the map* opens a
 * visible live view (satellite context, snapshot, HLS, or the NT msejub
 * iframe).
 *
 *   vendor=longdo,  imageUrl present  to  JPG snapshot <img> + Refresh
 *   vendor=longdo,  hlsUrl present   <Arrow direction="right" />  <video controls> with HLS source
 *                                         (Safari plays HLS natively; Chrome
 *                                          falls back to the snapshot URL).
 *   vendor=sikhio-nt, playerUrl      →  the NVR's live msejub player,
 *                                         embedded over HTTPS (see
 *                                         lib/cctvPlayer.ts for why the URL
 *                                         is rewritten).
 *
 * Offline cameras render a "this camera is offline at the source" state
 * instead of a broken player. The modal is sized as a true lightbox
 * (~720 px wide) so the stream is legible without scrolling.
 *
 * Closes on ESC button or backdrop (wired in App.tsx).
 */
export function CctvLiveModal({ camera, onClose }: Props) {
  // Longdo snapshot refresh counter (cache-busts the image URL).
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    setRefreshKey(0);
  }, [camera?.id]);

  if (!camera) return null;

  const online = camera.online;
  const label = online === undefined ? "—" : online ? "Online" : "Offline";
  const onlineTone =
    online === undefined ? "var(--text-3)" : online ? "var(--good)" : "var(--bad)";

  const isNt = camera.vendor === "sikhio-nt";
  const isLongdo = camera.vendor === "longdo";
  const vendorLabel = isNt ? "NT · msejub player" : isLongdo ? "Longdo · snapshot/HLS" : camera.vendor;

  return (
    <aside
      className="cctv-modal"
      role="dialog"
      aria-label={`CCTV: ${camera.name}`}
    >
      <header className="building-card-head">
        <div>
          <span className="eyebrow mono">SIKHIO · CCTV</span>
          <h3 className="building-card-title">{camera.name}</h3>
          <div className="building-card-alt">
            {camera.organization ?? vendorLabel}
            {camera.id ? ` · ${camera.id.replace(/^sikhio-/, "").replace(/^longdo-/, "")}` : ""}
          </div>
        </div>
        <button onClick={onClose} aria-label="ปิดมุมมองกล้อง" className="building-card-close mono">
          ปิด
        </button>
      </header>

      <dl className="building-card-meta mono">
        <dt>STATE</dt>
        <dd>
          <span
            className="cctv-state-dot"
            style={{ background: onlineTone }}
            aria-hidden="true"
          />
          {label}
        </dd>
        <dt>GPS</dt>
        <dd>
          {camera.lat.toFixed(5)}, {camera.lng.toFixed(5)}
        </dd>
        <dt>SOURCE</dt>
        <dd>{vendorLabel}</dd>
      </dl>

      {online === false ? (
        <div className="cctv-stream-empty">
          <p>This camera is offline at the source.</p>
          <p className="cctv-stream-hint">
            Status comes from the upstream (Longdo snapshot or NT NVR). When
            it comes back online the marker on the map turns green again.
          </p>
        </div>
      ) : isNt ? (
        /* NT msejub live player, embedded over HTTPS. */
        <NtLiveView camera={camera} />
      ) : isLongdo && camera.imageUrl ? (
        /* Longdo JPG snapshot — refresh to fetch the latest frame */
        <LongdoLiveView camera={camera} refreshKey={refreshKey} onRefresh={() => setRefreshKey((k) => k + 1)} />
      ) : isLongdo && camera.hlsUrl ? (
        /* Longdo HLS stream — Safari plays HLS natively */
        <div className="cctv-stream-frame">
          <video
            key={camera.id}
            controls
            autoPlay
            muted
            playsInline
            title={`Live CCTV: ${camera.name}`}
          >
            <source src={camera.hlsUrl} type="application/vnd.apple.mpegurl" />
            Your browser does not support HLS playback. Open the camera in a
            Safari tab or use the snapshot URL below.
          </video>
          <p className="cctv-stream-foot">
            <a href={camera.hlsUrl} target="_blank" rel="noopener noreferrer">
              Open HLS stream ↗
            </a>
          </p>
        </div>
      ) : (
        <div className="cctv-stream-empty">
          <p>No live view exposed for this camera.</p>
          <p className="cctv-stream-hint">
            The vendor ({camera.vendor}) hasn't published an image or stream URL
            for this camera. Marker + online state on the map still work.
          </p>
        </div>
      )}
    </aside>
  );
}

/**
 * Sikhio NT camera view.
 *
 * We play the NVR's MSE WebSocket ourselves (CctvMsePlayer) instead of
 * embedding the vendor's player page. Two earlier attempts failed in ways
 * worth recording, because both looked identical to the operator — a black
 * rectangle:
 *
 *  1. The vendor page was embedded over http:// inside our https:// app, so
 *     the browser blocked it outright (and our CSP frame-src did not allow
 *     the origin either). This got misdiagnosed as "the stream is
 *     intranet-only" and shipped as a satellite thumbnail stand-in.
 *  2. Embedded over https://, the page loads — but its <video> is unmuted,
 *     so Chrome's autoplay policy leaves it paused at 0:00 in any profile
 *     without Media Engagement on that origin. It played during development
 *     only because that profile had built engagement up.
 *
 * Owning a muted, autoplaying element removes both failure modes. The
 * open-in-new-tab link still points at the vendor page as an escape hatch.
 */
function NtLiveView({ camera }: { camera: CctvCamera }) {
  const stream = parsePlayerUrl(camera.playerUrl);
  const pageUrl = embeddablePlayerUrl(camera.playerUrl);

  if (!stream) {
    return (
      <div className="cctv-stream-empty">
        <p>กล้องนี้ไม่มีลิงก์ภาพสดจาก NVR</p>
        <p className="cctv-stream-hint">ตำแหน่งและสถานะออนไลน์บนแผนที่ยังใช้งานได้</p>
      </div>
    );
  }

  return (
    <>
      <div className="cctv-stream-frame cctv-stream-frame--nt">
        <CctvMsePlayer key={`${stream.uuid}-${stream.channel}`} stream={stream} name={camera.name} />
      </div>
      <p className="cctv-stream-foot">
        <span className="cctv-stream-foot-meta">
          ภาพสดจาก NVR เทศบาล · <code>{camera.id.replace(/^sikhio-/, "")}</code>
        </span>
        {pageUrl && (
          <a href={pageUrl} target="_blank" rel="noopener noreferrer">
            เปิดในแท็บใหม่ ↗
          </a>
        )}
      </p>
    </>
  );
}

/**
 * Longdo JPG snapshot viewer. The upstream exposes a static image URL
 * that updates server-side every few seconds; refreshing the URL with a
 * cache-busting query string gives the operator the latest frame on
 * demand. We avoid a continuous reload loop so the image doesn't
 * thrash the network while the modal is open.
 */
function LongdoLiveView({
  camera,
  refreshKey,
  onRefresh,
}: {
  camera: CctvCamera;
  refreshKey: number;
  onRefresh: () => void;
}) {
  const [imgError, setImgError] = useState(false);
  const [imgLoaded, setImgLoaded] = useState(false);

  useEffect(() => {
    setImgError(false);
    setImgLoaded(false);
  }, [camera.id, refreshKey]);

  if (!camera.imageUrl) return null;
  // Cache-bust so re-renders (or the Refresh button) re-fetch the latest
  // upstream frame instead of hitting the browser HTTP cache.
  const src = `${camera.imageUrl}${camera.imageUrl.includes("?") ? "&" : "?"}_=${refreshKey}`;

  return (
    <>
      <div className="cctv-stream-frame">
        <img
          key={`${camera.id}-${refreshKey}`}
          src={src}
          alt={`Live CCTV snapshot: ${camera.name}`}
          className="cctv-snapshot"
          onLoad={() => setImgLoaded(true)}
          onError={() => setImgError(true)}
        />
        {!imgLoaded && !imgError && (
          <p className="cctv-stream-hint cctv-stream-hint-overlay">Loading snapshot…</p>
        )}
        {imgError && (
          <p className="cctv-stream-hint cctv-stream-hint-overlay">
            Snapshot failed to load. Longdo image URLs sometimes return a
            blank GIF when the camera is being restarted.
          </p>
        )}
      </div>
      <p className="cctv-stream-foot">
        <button
          type="button"
          className="cctv-stream-refresh mono"
          onClick={onRefresh}
          aria-label={`Refresh snapshot for ${camera.name}`}
        >
          ↻ Refresh snapshot
        </button>
        <a href={camera.imageUrl} target="_blank" rel="noopener noreferrer">
          Open snapshot URL ↗
        </a>
        {camera.hlsUrl && (
          <a href={camera.hlsUrl} target="_blank" rel="noopener noreferrer">
            Open HLS stream ↗
          </a>
        )}
      </p>
    </>
  );
}